// src/services/staffService.js
import { apiRequest, uploadFile } from './api';

export const staffService = {
    // Get staff list with pagination and filters
    getStaff: async (params = {}) => {
        try {
            const response = await apiRequest.get('/staff', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch staff');
        }
    },

    // Get single staff member details
    getStaffById: async (staffId) => {
        try {
            const response = await apiRequest.get(`/staff/${staffId}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch staff details');
        }
    },

    // Get full staff activity details (profile + attendance + login events)
    getStaffActivity: async (staffId, params = {}) => {
        try {
            const response = await apiRequest.get('/staff/activity', {
                params: {
                    staff_id: staffId,
                    attendance_limit: params.attendance_limit || 100,
                    login_limit: params.login_limit || 100
                }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch staff activity');
        }
    },

    // Create new staff member
    createStaff: async (staffData) => {
        try {
            const payload = {
                ...staffData,
                passport_upload: 0
            };
            const response = await apiRequest.post('/staff', payload);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to create staff member');
        }
    },

    // Update staff member
    updateStaff: async (staffId, staffData) => {
        try {
            console.log("Staff data ", staffData);
            const response = await apiRequest.put(`/staff/${staffId}`, staffData);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to update staff member');
        }
    },

    // Delete staff member
    deleteStaff: async (staffId) => {
        try {
            const response = await apiRequest.delete(`/staff/${staffId}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to delete staff member');
        }
    },

    // Bulk upload staff data
    bulkUploadStaff: async (orgId, fileData, imageFiles = [], uploadMethod = 'separate') => {
        try {
            const formData = new FormData();
            formData.append('org_id', orgId);
            // REMOVED: formData.append('premises_id', premisesId); // No longer needed as it's in the CSV
            formData.append('upload_method', uploadMethod);

            if (uploadMethod === 'zip') {
                formData.append('zip_file', fileData);
            } else {
                formData.append('csv_file', fileData);
                imageFiles.forEach((file) => {
                    formData.append('image_files[]', file);
                });
            }

            const response = await apiRequest.post('/staff/bulk', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to upload staff bulk data');
        }
    },


    // Get bulk upload status
    getBulkUploadStatus: async (batchId) => {
        try {
            const response = await apiRequest.get('/bulk/status', {
                params: { batch_id: batchId }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to get upload status');
        }
    },

    // Export staff data
    exportStaff: async (format = 'csv', filters = {}) => {
        try {
            const params = {
                ...filters,
                export: format
            };
            const response = await apiRequest.get('/staff', {
                params,
                responseType: 'blob'
            });

            // Create download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `staff_export_${new Date().toISOString().split('T')[0]}.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return true;
        } catch (error) {
            throw new Error(error.message || 'Failed to export staff data');
        }
    },

    // Upload staff photo
    uploadStaffPhoto: async (staffId, photoFile) => {
        try {
            const formData = new FormData();
            formData.append('photo', photoFile);

            const response = await uploadFile(`/staff/${staffId}/photo`, formData);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to upload staff photo');
        }
    },

    // Update staff with photo
    updateStaffWithPhoto: async (staffId, staffData, photoFile) => {
        try {
            const formData = new FormData();

            // Add all staff data to FormData
            Object.keys(staffData).forEach(key => {
                if (staffData[key] !== null && staffData[key] !== undefined) {
                    formData.append(key, staffData[key]);
                }
            });

            formData.append('passport_upload', photoFile ? '1' : '0');

            // Add photo if provided
            if (photoFile) {
                formData.append('photo', photoFile);
            }

            const response = await apiRequest.put(`/staff/${staffId}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to update staff member');
        }
    },

    // Create staff with photo
    createStaffWithPhoto: async (staffData, photoFile) => {
        try {
            const formData = new FormData();

            // Add all staff data to FormData
            Object.keys(staffData).forEach(key => {
                if (staffData[key] !== null && staffData[key] !== undefined) {
                    formData.append(key, staffData[key]);
                }
            });

            formData.append('passport_upload', photoFile ? '1' : '0');

            // Add photo if provided
            if (photoFile) {
                formData.append('photo', photoFile);
            }

            const response = await apiRequest.post('/staff', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to create staff member');
        }
    },

    // Get staff statistics
    getStaffStats: async (filters = {}) => {
        try {
            const response = await apiRequest.get('/staff/stats', { params: filters });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch staff statistics');
        }
    },

    // Assign staff to shift
    assignStaffToShift: async (staffId, shiftId, effectiveDate) => {
        try {
            const response = await apiRequest.post('/staff/assign-shift', {
                staff_id: staffId,
                shift_id: shiftId,
                effective_from: effectiveDate
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to assign staff to shift');
        }
    },

    // Get staff shifts history
    getStaffShifts: async (staffId) => {
        try {
            const response = await apiRequest.get(`/staff/${staffId}/shifts`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch staff shifts');
        }
    },

    // Get staff transfer history
    getStaffTransfers: async (params = {}) => {
        try {
            const response = await apiRequest.get('/staff/transfers', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch staff transfers');
        }
    },

    // Export staff transfers
    exportStaffTransfers: async (format = 'csv', filters = {}) => {
        try {
            const params = {
                ...filters,
                export: format
            };
            const response = await apiRequest.get('/staff/transfers', {
                params,
                responseType: 'blob'
            });

            // Create download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `staff_transfers_${new Date().toISOString().split('T')[0]}.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return true;
        } catch (error) {
            throw new Error(error.message || 'Failed to export staff transfers');
        }
    }
};
